import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Building, Heart, Headphones, Check, ArrowRight } from 'lucide-react';

const PARTNER_CATEGORIES = [
  {
    title: 'Funeral Homes',
    description: 'Trusted funeral service providers offering comprehensive end-of-life care.',
    icon: Building,
  },
  {
    title: 'Memorial Services',
    description: 'Specialists in creating meaningful remembrance ceremonies.',
    icon: Heart,
  },
  {
    title: 'Support Services',
    description: 'Professional grief counseling and family support services.',
    icon: Headphones,
  },
  {
    title: 'Community Organizations',
    description: 'Local organizations dedicated to serving families in need.',
    icon: Users,
  },
];

const BENEFITS = [
  {
    image: '/images/partners/collaboration.jpg',
    alt: 'Service providers collaborating',
    title: 'For Service Providers',
    points: [
      'Access to a network of trusted professionals and shared expertise',
      'Collaborative business opportunities and growth potential',
      'Access to comprehensive resources and support systems',
    ],
  },
  {
    image: '/images/partners/support.jpg',
    alt: 'Family receiving support',
    title: 'For Families',
    points: [
      'Seamless coordination of comprehensive memorial services',
      'Access to vetted, quality-assured service providers',
      'Enhanced support through our integrated network of care',
    ],
  },
];

const Partners = () => (
  <div className="bg-white">
    {/* Hero */}
    <div className="relative h-[500px]">
      <img
        src="/images/partners/partnership.jpg"
        alt="Colleagues meeting to discuss a partnership"
        className="absolute h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950/85 via-navy-950/70 to-navy-950/80" />
      <div className="container relative mx-auto flex h-full flex-col items-center justify-center px-4 text-center">
        <p className="eyebrow animate-fade-up text-navy-100">Working together</p>
        <h1 className="mt-4 animate-fade-up text-4xl text-white md:text-5xl">Our Partners</h1>
        <p className="mt-5 max-w-2xl animate-fade-up font-montserrat leading-relaxed text-slate-200 md:text-lg">
          Working together to provide comprehensive memorial and cemetery services with dignity
          and compassion.
        </p>
      </div>
    </div>

    {/* Introduction */}
    <section className="py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 md:grid-cols-2 lg:gap-16">
          <div>
            <p className="eyebrow">A shared commitment</p>
            <h2 className="mt-3 text-3xl md:text-4xl">Building Strong Partnerships</h2>
            <p className="mt-6 font-montserrat leading-relaxed text-slate-600">
              At Cemetery &amp; Burial Solutions, we carefully select our partners to ensure the
              highest quality of service for our clients and community members. Our network of
              trusted professionals helps us deliver comprehensive care and support.
            </p>
            <p className="mt-4 font-montserrat leading-relaxed text-slate-600">
              Through collaborative relationships with funeral homes, memorial service providers,
              and community organizations, we create a seamless experience for families during
              their time of need.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl shadow-card">
            <img
              src="/images/partners/community.jpg"
              alt="A community gathering in support of one another"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>

    {/* Partner Categories */}
    <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-14 max-w-2xl text-center">
          <p className="eyebrow">Who we work with</p>
          <h2 className="mt-3 text-3xl md:text-4xl">Our Network of Care</h2>
          <div className="rule mt-5" />
        </div>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {PARTNER_CATEGORIES.map((category) => {
            const Icon = category.icon;
            return (
              <div
                key={category.title}
                className="group flex flex-col items-center rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-navy-200 hover:shadow-card-hover"
              >
                <span className="icon-chip transition-transform duration-300 group-hover:scale-110 group-hover:bg-navy-100">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-xl">{category.title}</h3>
                <p className="mt-2 font-montserrat text-sm leading-relaxed text-slate-600">
                  {category.description}
                </p>
                <div className="mt-auto pt-6">
                  <span className="inline-block rounded-full bg-navy-50 px-4 py-1.5 font-montserrat text-xs font-medium tracking-wide text-navy-700 ring-1 ring-inset ring-navy-100">
                    Coming Soon
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Partnership Benefits */}
    <section className="py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <p className="eyebrow">Why partner with us</p>
            <h2 className="mt-3 text-3xl md:text-4xl">The Value of Partnership</h2>
            <div className="rule mt-5" />
          </div>
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            {BENEFITS.map((benefit) => (
              <div
                key={benefit.title}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card"
              >
                <img
                  src={benefit.image}
                  alt={benefit.alt}
                  className="aspect-[16/9] w-full object-cover"
                />
                <div className="p-8">
                  <h3 className="text-2xl">{benefit.title}</h3>
                  <ul className="mt-5 space-y-3.5">
                    {benefit.points.map((point) => (
                      <li key={point} className="flex items-start gap-3">
                        <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-navy-600" />
                        <span className="font-montserrat text-sm leading-relaxed text-slate-600">
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Call to Action */}
    <section className="relative overflow-hidden bg-navy-gradient py-24 text-center text-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(50% 60% at 50% 0%, rgba(78,143,233,0.35) 0%, rgba(8,45,104,0) 70%)',
        }}
      />
      <div className="container relative mx-auto px-4">
        <div className="mx-auto max-w-3xl">
          <p className="eyebrow text-navy-200">Become a partner</p>
          <h2 className="mt-3 text-3xl text-white md:text-4xl">Join Our Network of Care</h2>
          <p className="mx-auto mt-4 max-w-2xl font-montserrat leading-relaxed text-navy-100">
            We&rsquo;re always looking to expand our network of quality service providers who share
            our commitment to excellence and compassionate care. Partner with us to make a
            difference in the lives of families during their time of need.
          </p>
          <Link to="/contact" className="btn-on-dark mt-8">
            Explore Partnership Opportunities
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default Partners;
